import { useEffect } from 'react'
import { cn } from '@/helpers/shadcn/utils'
import { cva } from 'class-variance-authority'
import { Icon } from '@/components/atoms'

/**
 * Alert component to display different types of alert messages.
 *
 * @param {Object} props - The properties object.
 * @param {'success' | 'error' | 'info' | 'warning'} props.type - The type of alert to display.
 * @param {string} props.message - The message to display in the alert.
 * @param {boolean} [props.show=false] - Whether the alert is visible or not.
 * @param {Function} props.onCloseClick - The function to call when the close button is clicked.
 * @param {number} [props.duration=5000] - Auto-close duration in milliseconds.
 * @param {boolean} [props.showCloseButton=true] - Whether to show the close button.
 *
 * @returns {JSX.Element|null} The Alert component or null if not visible.
 */
const ToasterAlert = ({ 
  type, 
  message, 
  show = false, 
  onCloseClick, 
  duration = 5000, 
  showCloseButton = true 
}) => {
  useEffect(() => {
    if (!show) return
    const itv = setTimeout(() => onCloseClick?.(), duration)
    return () => clearTimeout(itv)
  }, [message, onCloseClick, show, type, duration])

  const alertVariants = cva(
    `relative z-30 flex items-start w-full rounded-md px-4 py-2 alert max-w-[calc(100vw-7.5rem)]`,
    {
      variants: {
        type: {
          success: 'bg-support-success-primary text-white',
          error: 'bg-support-error-primary text-white',
          info: 'bg-support-info-primary text-white',
          warning: 'bg-[#CC6200] text-white',
        },
      },
    }
  )

  const TYPES = {
    SUCCESS: 'success',
    ERROR: 'error',
    INFO: 'info',
    WARNING: 'warning'
  }

  const iconTypes = {
    [TYPES.SUCCESS]: 'check_circle_outline',
    [TYPES.ERROR]: 'error',
    [TYPES.INFO]: 'info',
    [TYPES.WARNING]: 'warning_amber'
  }

  if (!show) return null

  return (
    <div
      data-testid={`alert-${type}`}
      className={cn(alertVariants({ type }))}
      role="alert"
    >
      <div className="inline-flex h-6 w-6 items-center justify-center rounded-lg mt-0.5">
        <Icon icon={iconTypes[type]} size="medium" className="text-button-icon" />
      </div>
      <div className={`text-md ml-3 flex-1 py-1 items-center ${showCloseButton ? '' : 'pr-4'}`}>
        {message && (
          <div className={`text-sm whitespace-pre-line text-white`}>
            {message}
          </div>
        )}
      </div>
      {showCloseButton && (
        <button
          type="button"
          className="ml-4 mt-1 inline-flex h-4 w-4 items-center justify-center text-icon"
          data-testid="alert-close-btn"
          aria-label="Close"
          onClick={() => onCloseClick?.()}
        >
          <span className="sr-only">Close</span>
          <Icon
            icon="close"
            size="small"
            style={{color: 'white'}}
            className={type === 'warning' ? 'text-text-primary' : 'text-button-icon'}
          />
        </button>
      )}
    </div>
  )
}

export default ToasterAlert
